import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getDistanceFromLine, scaledCoordinate } from '../drawing.helper'
import { primaryDrawingColor } from '../../../../assets/colors'

export type PriceNoteState = {
    p1?: Point,
    p2?: Point
}

export type PriceNoteSettings = {
    lineColor: ColorValue,
    lineWidth: number,
    point1: Point,
    point2: Point,
    labelColor: ColorValue
}

const THRESHOLD = 10 // cursor distance threshold for treating as hovered or not

class PriceNoteTool extends DrawingBase<any, PriceNoteSettings, PriceNoteState> {
    slope: number = 0
    rotation = 0
    intercept = 0
    scaledCoordinates: Record<string, PointCoordinates> = {}
    labelStartX = 0
    labelStartY = 0
    boundingBoxWidth = 0
    boundingBoxHeight = 0

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        const { p1, p2 } = this.scaledCoordinates
        if (!p1 || !p2) return
        ctx.beginPath()
        this.setLineAttributes(ctx)
        ctx.moveTo(p1.x, p1.y)
        ctx.lineTo(p2.x, p2.y)
        ctx.stroke()
        ctx.closePath()
        this.renderLabel(ctx)
    };

    calculateLineAttributes() {
        const { p1, p2 } = this._state
        if (p1) {
            let p1x = this.timeToCoordinate(p1.x)
            let p1y = this.priceToCoordinate(p1.y)

            if (p1x && p1y) {
                this.scaledCoordinates.p1 = {
                    x: scaledCoordinate(p1x, this.horizontalPixelRatio),
                    y: scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
                }
            }
        }

        if (p1 && p2) {
            let p2x = this.timeToCoordinate(p2.x)
            let p2y = this.priceToCoordinate(p2.y)

            if (p2x && p2y) {
                this.scaledCoordinates.p2 = {
                    x: scaledCoordinate(p2x, this.horizontalPixelRatio),
                    y: scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
                }

                const { p1: { x: p1x, y: p1y } } = this.scaledCoordinates

                this.slope = (p2y - p1y) / (p2x - p1x)
                this.intercept = p1y - this.slope * p1x
                this.rotation = Math.atan(this.slope)
            }
        }
    }

    setLineAttributes(ctx: CanvasRenderingContext2D) {
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio
        ctx.strokeStyle = this._settings.lineColor.color
    }

    renderLabel(ctx: CanvasRenderingContext2D) {
        const { p1, p2 } = this._state
        const { p1: p1s, p2: p2s } = this.scaledCoordinates
        if (!p1 || !p2 || !p1s || !p2s) return

        const devicePixelRatio = window.devicePixelRatio
        const padding = 6 * devicePixelRatio
        ctx.lineWidth = 0.5
        ctx.font = `${12 * devicePixelRatio}px sans-serif`
        const labelText = p2.y.toFixed(2)

        let labelTextDimensions = ctx.measureText(labelText)
        const labelTextHeight = labelTextDimensions.actualBoundingBoxAscent + labelTextDimensions.actualBoundingBoxDescent
        const boundingHeight = labelTextHeight + 2 * padding
        const boundingWidth = labelTextDimensions.width + 2 * padding

        const [startX, startY] = this.getLabelPosition(boundingWidth, boundingHeight)
        this.labelStartX = startX
        this.labelStartY = startY
        this.boundingBoxWidth = boundingWidth
        this.boundingBoxHeight = boundingHeight

        ctx.roundRect(startX, startY, boundingWidth, boundingHeight, 3 * window.devicePixelRatio)
        ctx.fillStyle = this._settings.labelColor.color
        ctx.globalAlpha = this._settings.labelColor.opacity
        ctx.fill()
        ctx.globalAlpha = 1
        ctx.fillStyle = '#fff'
        ctx.textBaseline = 'top'
        ctx.fillText(labelText, startX + padding, startY + padding)
    }

    getLabelPosition(boundingWidth: number, boundingHeight: number): [number, number] {
        const { p1, p2 } = this._state
        let { p1: p1s, p2: p2s } = this.scaledCoordinates
        if (!p1 || !p2 || !p1s || !p2s) return [0, 0]

        let startX = 0, startY = 0
        if (Math.abs(this.slope) < 1) {
            startY = p2s.y - boundingHeight / 2
            if (p2s.x > p1s.x) {
                startX = p2s.x
            } else {
                startX = p2s.x - boundingWidth
            }
        } else {
            startX = p2s.x - boundingWidth / 2
            if (p2s.y > p1s.y) {
                startY = p2s.y
            } else {
                startY = p2s.y - boundingHeight
            }
        }
        return [startX, startY]
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const { p1 } = this._state
        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!p1) {
            this._state.p1 = point
        } else {
            // Preview may already have set p2 — still commit and end on the real second click.
            point.id = 'p2'
            this._state.p2 = point
            if (!preview) this.onDrawingEnd()
        }
        if (!preview) return { ...point }
    }

    recalculateHandles = () => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return

        const p1x = this.timeToCoordinate(p1.x)
        const p2x = this.timeToCoordinate(p2.x)
        const p1y = this.priceToCoordinate(p1.y)
        const p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.slope = (p2y - p1y) / (p2x - p1x)
            this.intercept = p1y - this.slope * p1x
            this.rotation = Math.atan(this.slope)
        }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2 } = this._state
        if (p1) {
            handles.push(p1)
        }

        p2 && handles.push(p2)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { p1, p2 } = this.scaledCoordinates
        if (!p1 || !p2) return null

        let minX = p1.x, maxX = p2.x, minY = p1.y, maxY = p2.y

        if (maxX < minX) {
            minX = p2.x
            maxX = p1.x
        }

        if (maxY < minY) {
            minY = p2.y
            maxY = p1.y
        }


        const distance = getDistanceFromLine(scaledX, scaledY, this.slope, this.intercept)

        if ((scaledX >= this.labelStartX && scaledX <= this.labelStartX + this.boundingBoxWidth
                && scaledY >= this.labelStartY && scaledY <= this.labelStartY + this.boundingBoxHeight)
            || (
                (scaledX >= minX - THRESHOLD / 3 && scaledX <= maxX + THRESHOLD / 3 || scaledY >= minY - THRESHOLD / 3 || scaledY <= maxY + THRESHOLD / 3)
                && distance <= THRESHOLD
            )) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }
        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h

            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return
        switch (id) {
            case 'p1':
                p1.x = time
                p1.y = price
                break
            case 'p2':
                p2.x = time
                p2.y = price
                break
        }
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []
        const min = Math.min(p1.y, p2.y)
        const max = Math.max(p1.y, p2.y)
        if (!min || !max) return []
        return [{
            id: 'price',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.lineColor.color,
            labelColors: {
                p1: this._settings.lineColor.color,
                p2: this._settings.lineColor.color
            }
        }]
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []
        const p1x = this.timeToCoordinate(p1.x)
        const p2x = this.timeToCoordinate(p2.x)

        if (!p1x || !p2x) return []
        let min: Time = p1.x, max: Time = p2.x
        if (p1x > p2x) {
            min = p2.x
            max = p1.x
        }
        if (!min || !max) return []

        return [{
            id: 'time',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.lineColor.color,
            labelColors: {
                p1: this._settings.lineColor.color,
                p2: this._settings.lineColor.color
            }
        }]
    }

    onSettingsUpdate = (settings: Partial<PriceNoteSettings>) => {
        if (settings.point1) {
            this._state.p1 = settings.point1
        }
        if (settings.point2) {
            this._state.p2 = settings.point2
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<PriceNoteSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                },
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: '#1 (price, bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p1',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: true
                    }
                },
                {
                    name: 'point2',
                    label: '#2 (price, bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p2',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: true
                    }
                }
            ]
        }]
    }

    getSettingsValues(): PriceNoteSettings {
        const settings = { ...this._settings }
        if (this._state.p1) {
            settings.point1 = this._state.p1
        }
        if (this._state.p2) {
            settings.point2 = this._state.p2
        }
        return settings
    }

    getDrawingType(): DrawingTypes {

        return 'priceNote'
    }

    getMiniSettings = () => {
        return ['lineColor', 'lineWidth', 'labelColor'] as (keyof PriceNoteSettings)[]
    }
}

export default PriceNoteTool